// packages/shared-hooks/src/users/useDeactivateUser.ts
// Spec §18, §14.4. POST /users/:id/deactivate with a required `reason`.
// Invalidates list + detail so the row flips to "Неактивен" and the drawer
// refetches the new state.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserActivationResponseSchema, type UserActivationResponse } from '@magimp/shared';
import { useApiClient } from '../api/context.js';

export interface DeactivateUserInput {
  reason: string;
}

// WHY: backend returns `{ changed: boolean; user: User }` from this endpoint
// (`apps/backend/src/users/users.controller.ts`); the previous local
// `{ active: false }` declaration was a TypeScript-only contract that masked
// the real wire shape. Aligning here so schema parsing accepts the actual
// server response — `changed: false` is the legitimate idempotent no-op
// when the user was already inactive, and consumers should rely on the
// embedded `user` projection rather than the input-derived literal.
export type DeactivateUserResult = UserActivationResponse;

export function useDeactivateUser(id: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<DeactivateUserResult, unknown, DeactivateUserInput>({
    mutationFn: (input) =>
      client.post<DeactivateUserResult>(
        `/users/${id}/deactivate`,
        input,
        {},
        UserActivationResponseSchema,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      qc.invalidateQueries({ queryKey: ['user', id] });
    },
  });
}
